import * as THREE from 'three';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

export interface TextureVariant {
  width: number; height: number; legacyUrl: string;
  ktx2: { standard: string; flipped: string };
  outputs?: { standard?: { bytes: number }; flipped?: { bytes: number } };
}
export interface PerformanceAssets { version: number; textures: Record<string, TextureVariant> }
interface VariantOptions { maxConcurrent?: number; maxPendingBytes?: number; timeoutMs?: number }
interface VariantJob { path: string; bytes: number; resolve: (texture: THREE.CompressedTexture | undefined) => void }

/** The pilot's files (`npm run assets:performance`) are not in git. vite.config.ts tells the bundle
 * whether this build has the manifest; one without it never asks for it, so it logs no 404. */
export const textureVariantsBuilt = typeof __HP_TEXTURE_VARIANTS__ !== 'undefined' && __HP_TEXTURE_VARIANTS__;

export class TextureVariants {
  private manager = new THREE.LoadingManager();
  private loader = new KTX2Loader(this.manager);
  private controller = new AbortController();
  private manifest: Promise<PerformanceAssets | undefined>;
  private disposed = false;
  private queue: VariantJob[] = [];
  private active = new Map<VariantJob, (texture?: THREE.CompressedTexture) => void>();
  private pendingBytes = 0;
  private manifestTimer?: ReturnType<typeof setTimeout>;
  completed = 0;
  fallbacks = 0;

  constructor(renderer: THREE.WebGLRenderer, private options: VariantOptions = {}) {
    this.loader.setTranscoderPath('/hp/performance/basis/').setWorkerLimit(2).detectSupport(renderer);
    this.manifest = new Promise(resolve => {
      this.manifestTimer = setTimeout(() => { this.controller.abort(); resolve(undefined); }, this.options.timeoutMs ?? 20_000);
      fetch('/hp/performance/manifest.json', { signal: this.controller.signal, cache: 'no-cache' })
        .then(response => response.ok ? response.json() as Promise<PerformanceAssets> : undefined).catch(() => undefined)
        .then(value => { clearTimeout(this.manifestTimer); resolve(value); });
    });
  }

  async load(url: string, flipY: boolean): Promise<THREE.CompressedTexture | undefined> {
    const variant = (await this.manifest)?.textures?.[url];
    if (!variant || this.disposed) return undefined;
    const orientation = flipY ? 'flipped' : 'standard';
    // Reserve the encoded payload plus worst-case RGBA transcode/mips. The
    // result is counted later from its real GPU format, never from file size.
    const bytes = Math.ceil(variant.width * variant.height * 4 * 4 / 3) + (variant.outputs?.[orientation]?.bytes ?? 0);
    return new Promise(resolve => { this.queue.push({ path: variant.ktx2[orientation], bytes, resolve }); this.drain(); });
  }

  stats() { return { compressedPendingLoads: this.active.size, compressedQueuedLoads: this.queue.length,
    compressedPendingBytes: this.pendingBytes, compressedPendingBudgetBytes: this.options.maxPendingBytes ?? 32 * 1024 * 1024 }; }

  private drain(): void {
    if (this.disposed) return;
    const limit = Math.min(2, Math.max(1, this.options.maxConcurrent ?? 2)), budget = this.options.maxPendingBytes ?? 32 * 1024 * 1024;
    while (this.queue.length && this.active.size < limit) {
      if (this.active.size && this.pendingBytes + this.queue[0].bytes > budget) return;
      const job = this.queue.shift()!; let finished = false;
      const finish = (texture?: THREE.CompressedTexture) => {
        if (finished) { texture?.dispose(); return; }
        finished = true; clearTimeout(timer); this.active.delete(job); this.pendingBytes -= job.bytes;
        if (texture && !this.disposed && texture.isCompressedTexture) { texture.userData.performanceVariant = job.path; this.completed++; job.resolve(texture); }
        else { texture?.dispose(); this.fallbacks++; job.resolve(undefined); }
        this.drain();
      };
      // A stuck worker must not outlive the owner or let additional jobs exceed
      // the budget. Stop this optional pilot and retain ready legacy textures.
      const timer = setTimeout(() => this.dispose(), this.options.timeoutMs ?? 20_000);
      this.active.set(job, finish); this.pendingBytes += job.bytes;
      this.loader.loadAsync(job.path).then(finish, () => finish());
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; clearTimeout(this.manifestTimer); this.controller.abort(); this.manager.abort();
    for (const finish of [...this.active.values()]) finish();
    for (const job of this.queue) { this.fallbacks++; job.resolve(undefined); }
    this.queue = []; this.loader.dispose();
  }
}
